import { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";

export const PdfViewerModal = ({ url, title, onClose }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [ctype, setCtype] = useState("application/pdf");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!url) return undefined;
    let objUrl = null;
    let cancelled = false;
    setBlobUrl(null);
    setErr("");
    (async () => {
      try {
        // fetch through axios so auth cookies + token refresh apply (plain new-tab GETs 401)
        const path = url.startsWith("http") ? url.replace(api.defaults.baseURL, "") : url;
        const resp = await api.get(path, { responseType: "blob" });
        if (cancelled) return;
        const ct = (resp.headers?.["content-type"] || "application/pdf").split(";")[0];
        setCtype(ct);
        objUrl = URL.createObjectURL(new Blob([resp.data], { type: ct }));
        setBlobUrl(objUrl);
      } catch (e) {
        if (!cancelled) setErr(formatApiError(e));
      }
    })();
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [url]);

  if (!url) return null;
  const ext = ctype.includes("png") ? "png" : ctype.includes("jpeg") ? "jpg" : ctype.includes("webp") ? "webp" : "pdf";
  const filename = `${(title || "document").replace(/[^a-z0-9]+/gi, "-").replace(/-+/g, "-").toLowerCase()}.${ext}`;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="pdf-viewer-modal" onClick={onClose}>
      <div className="flex h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-stone-200 px-4 py-2.5">
          <p className="truncate text-sm font-semibold text-stone-800" data-testid="pdf-viewer-title">{title}</p>
          <div className="flex items-center gap-3">
            {blobUrl && (
              <a href={blobUrl} download={filename} className="text-xs font-medium text-primary hover:underline" data-testid="pdf-viewer-download">Download</a>
            )}
            {blobUrl && (
              <a href={blobUrl} target="_blank" rel="noreferrer" className="text-xs font-medium text-primary hover:underline" data-testid="pdf-viewer-open-tab">Open in new tab</a>
            )}
            <button onClick={onClose} className="text-stone-400 hover:text-stone-700" data-testid="pdf-viewer-close"><X size={18} /></button>
          </div>
        </div>
        {err ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 rounded-b-lg bg-stone-100 p-8 text-center">
            <p className="text-sm font-medium text-[#C0392B]" data-testid="pdf-viewer-error">{err}</p>
            <p className="text-xs text-stone-500">Close this and try again — if it persists, sign out and back in.</p>
          </div>
        ) : blobUrl ? (
          <object data={blobUrl} type={ctype} className="min-h-0 flex-1 rounded-b-lg bg-stone-100" data-testid="pdf-viewer-frame">
            <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
              <p className="text-sm text-stone-600">Your browser blocked the inline preview.</p>
              <div className="flex gap-2">
                <a href={blobUrl} target="_blank" rel="noreferrer" className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground" data-testid="pdf-viewer-fallback-open">Open PDF</a>
                <a href={blobUrl} download={filename} className="rounded-md border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700" data-testid="pdf-viewer-fallback-download">Download PDF</a>
              </div>
            </div>
          </object>
        ) : (
          <div className="flex min-h-0 flex-1 items-center justify-center rounded-b-lg bg-stone-100">
            <p className="text-sm text-stone-500" data-testid="pdf-viewer-loading">Loading document…</p>
          </div>
        )}
      </div>
    </div>
  );
};
